import getIconOverviewPage from "@/components/icon-page/getIconOverviewPage";
import { IconPageEntity } from "@/common/contracts/strapi.contracts";
import { GetStaticPaths, GetStaticProps } from "next";
import { composeProps } from "next-compose-props";
import getTranslations from "../../../lib/getTranslations";
import searchProductsQuery from "../../../src/Apollo/query/searchProducts.graphql";
import Icon from "../../../src/components/icon-page/Icon";
import { apolloClientToProps, initializeApollo } from "../../../lib/apollo";
import { getIcon } from "@/components/icon-page/getIcon";
import { lowerCase } from "lodash-es";

type IconPageProps = {
  icon: IconPageEntity | null;
};

const IconPage = ({ icon }: IconPageProps) => {
  return <Icon icon={icon} />;
};

const getIconFromStrapi: GetStaticProps = async ({ params, locale }) => {
  const { slug } = params ?? {};

  if (typeof slug !== "string") {
    return { notFound: true };
  }

  const icon = await getIcon(locale, slug);

  if (icon) {
    return { props: { icon } };
  }

  // If we don't have icon is strapi we try to find dutch icon page to show
  // as fallback.
  const nlIcon = await getIcon("nl", slug);

  if (nlIcon) {
    return { props: { icon: nlIcon } };
  }

  return { notFound: true };
};

const getIconProductsFromBackend: GetStaticProps = async ({
  locale,
  params,
}) => {
  const { slug } = params ?? {};
  const client = initializeApollo();

  await client.query({
    query: searchProductsQuery,
    variables: {
      input: {
        query: lowerCase(slug),
        pagination: { limit: 10 },
      },
      lang: locale?.toUpperCase(),
    },
  });

  return apolloClientToProps(client);
};

const iconTranslations: GetStaticProps = async ({ locale }) => {
  return {
    props: await getTranslations(locale, ["icon-page"]),
  };
};

export const getStaticProps = composeProps(
  getIconFromStrapi,
  getIconProductsFromBackend,
  iconTranslations
);

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const overview = await getIconOverviewPage();
  const paths = [];

  const isString = (value?: string | null): value is string => {
    return typeof value === "string";
  };

  /** Generate pages only for those slugs we have an overview for. */
  const slugs =
    overview.attributes?.icons
      ?.map(icon => icon?.url?.replace("/icon/", ""))
      .filter<string>(isString) ?? [];

  for (const slug of slugs) {
    for (const locale of locales ?? []) {
      paths.push({
        params: { slug },
        locale,
      });
    }
  }

  return {
    paths,
    fallback: false,
  };
};

export default IconPage;
